import { ItemListAndDetails } from '../../commons/components/ItemListAndDetails';
import {
  BusinessPlanSurveySectionGuidanceKeys,
  examplesMap,
  instructionsMap,
  sectionsMap,
  SURVEY_ITEM_FINANCIAL_ANALYSIS,
  SURVEY_ITEM_INTRO,
  SURVEY_ITEM_REVIEW,
  SURVEY_ITEM_SECTION,
  SURVEY_ITEM_VARIABLES,
  SurveyItemViewModel,
  SurveyViewModel,
} from '../types';
import { useFetchedSurvey } from '../hooks/useFetchedSurvey';
import { useCallback, useEffect } from 'react';
import { SurveyItemList } from '../components/SurveyItemList';
import { SurveyItem } from '../components/SurveyItem';
import { useMemoCompare } from '../../hooks/useMemoCompare';
import _ from 'lodash';
import { useUpdateSurveyMutation } from '../hooks/useUpdateSurveyMutation';
import { useSurveyWithLocalChanges } from '../hooks/useSurveyWithLocalChanges';
import { useInterval } from '../../hooks/useInterval';
import { useFetchedPromptVariables } from '../../growegy-ai/variables/hooks/useFetchedPromptVariables';
import { VARIABLE_METADATAS } from '../../growegy-ai/types';
import { getDefaultVariableValue, isPromptVariableFilled } from '../../growegy-ai/util';
import { isSurveySectionDone } from '../utils';

export const BusinessPlan = () => {
  const {
    query: { data: survey, status: surveyStatus },
    queryStatusComponent: surveyLoadingComponent,
  } = useFetchedSurvey();

  const {
    query: { data: variables },
  } = useFetchedPromptVariables();

  const surveyViewModel: SurveyViewModel | null =
    survey && survey.sections.length > 0
      ? {
          sections: survey.sections.map((x) => ({
            type: 'section',
            ...x,
            metadata: {
              instruction: instructionsMap[x.key].publicContent,
              examples: examplesMap[x.key],
            },
          })),
          version: survey.version,
        }
      : null;

  const {
    saveSurveyMutation: { mutate: saveSurvey },
  } = useSurveyWithLocalChanges();
  const { mutate: updateSurvey } = useUpdateSurveyMutation();

  useInterval(saveSurvey, 20_000);
  useEffect(() => {
    return () => saveSurvey();
  }, [saveSurvey]);

  useEffect(() => {
    if (
      (!survey || (survey.sections.length === 0 && survey.version === null)) &&
      surveyStatus === 'success'
    ) {
      updateSurvey({
        sections: BusinessPlanSurveySectionGuidanceKeys.map((x) => ({
          key: x,
          section: sectionsMap[x].section,
          subsection: sectionsMap[x].subsection,
          value: '',
        })),
        version: survey?.version || null,
      });
    }
  }, [survey, surveyStatus, updateSurvey]);

  const idGetter = useCallback((item: SurveyItemViewModel) => {
    switch (item.type) {
      case SURVEY_ITEM_SECTION:
        return item.key;
      default:
        return item.type;
    }
  }, []);

  const titleClassNameGetter = useCallback((item: SurveyItemViewModel) => {
    switch (item.type) {
      case SURVEY_ITEM_INTRO:
      case SURVEY_ITEM_FINANCIAL_ANALYSIS:
        return 'survey-info__header';
      default:
        return '';
    }
  }, []);

  const nameGetter = useCallback((item: SurveyItemViewModel) => {
    switch (item.type) {
      case SURVEY_ITEM_INTRO:
        return 'Introduction';
      case SURVEY_ITEM_VARIABLES:
        return 'Personalization';
      case SURVEY_ITEM_SECTION:
        return item.subsection;
      case SURVEY_ITEM_FINANCIAL_ANALYSIS:
        return 'Financial Analysis';
      case SURVEY_ITEM_REVIEW:
        return 'Review & Download';
    }
  }, []);

  const categoryGetter = useCallback((item: SurveyItemViewModel) => {
    switch (item.type) {
      case SURVEY_ITEM_SECTION:
        return item.section;
      default:
        return null;
    }
  }, []);

  const items: SurveyItemViewModel[] = useMemoCompare(
    surveyViewModel
      ? [
          { type: SURVEY_ITEM_INTRO },
          { type: SURVEY_ITEM_VARIABLES },
          ...surveyViewModel.sections,
          { type: SURVEY_ITEM_REVIEW, version: surveyViewModel.version },
          { type: SURVEY_ITEM_FINANCIAL_ANALYSIS },
        ]
      : [],
    (a, b) => _.isEqual(a, b)
  );

  const surveySectionItems = items.flatMap((x) => {
    if (x.type === SURVEY_ITEM_SECTION) {
      return [x];
    }
    return [];
  });

  const businessPlanPersonalisationVariables = VARIABLE_METADATAS.filter(
    (x) => x.isRequiredForBusinessPlan
  ).flatMap((x) => {
    if (!variables) return [];
    const variable = variables.find((v) => v.variableValue.name === x.name) ?? {
      id: null,
      isPrivate: false,
      variableValue: getDefaultVariableValue(x.name),
    };
    return [variable];
  });

  const businessPlanFilledPersonalisationVariables = businessPlanPersonalisationVariables.filter(
    (v) => isPromptVariableFilled(v)
  );

  const progressBar100 = surveySectionItems.length + businessPlanPersonalisationVariables.length;
  const progressBarValue =
    surveySectionItems.filter((x) => isSurveySectionDone(x)).length +
    businessPlanFilledPersonalisationVariables.length;

  const currentProgressBarValue = surveyViewModel
    ? Math.max(1, Math.floor((progressBarValue / progressBar100) * 100))
    : undefined;

  return (
    <div className="business-plan__container">
      <ItemListAndDetails
        items={items || null}
        itemsLoadingComponent={surveyLoadingComponent}
        itemListExtraProps={{ surveyVersion: surveyViewModel?.version }}
        itemListComponent={SurveyItemList}
        detailsComponent={SurveyItem}
        nameGetter={nameGetter}
        idGetter={idGetter}
        categoryGetter={categoryGetter}
        headerClassNameGetter={titleClassNameGetter}
        progress={currentProgressBarValue}
      />
    </div>
  );
};
